/* Price labels for Funny & Naughty Christmas Sweaters. Raw option values stay intact. */
(function () {
  'use strict';

  const isSize = name => /\bsize\b/i.test(name);
  function range(rows) {
    const prices = rows.map(row => row.price).filter(Number.isFinite);
    return prices.length ? { min: Math.min(...prices), max: Math.max(...prices) } : null;
  }

  function matchingVariants(variants, selected, index, value) {
    return variants.filter(row => row.available !== false && row.options[index] === value
      && row.options.every((other, otherIndex) => (
        otherIndex === index || !selected[otherIndex] || other === selected[otherIndex]
      )));
  }

  function getOptionAvailability(optionNames, variants, selected) {
    return optionNames.map((_, index) => Object.fromEntries(
      [...new Set(variants.map(row => row.options[index]))]
        .map(value => [value, matchingVariants(variants, selected, index, value).length > 0])
    ));
  }

  function getOptionRanges(optionNames, variants, selected) {
    return optionNames.map((name, index) => {
      const values = [...new Set(variants.map(row => row.options[index]))];
      if (!isSize(name)) {
        const purchasable = variants.filter(row => row.available !== false);
        const byValue = values.map(value => JSON.stringify(range(purchasable.filter(row => row.options[index] === value))));
        const combinations = new Map();
        let changesPrice = new Set(byValue).size > 1;
        for (const row of purchasable) {
          const key = JSON.stringify(row.options.filter((_, i) => i !== index));
          if (combinations.has(key) && combinations.get(key) !== row.price) changesPrice = true;
          combinations.set(key, row.price);
        }
        if (!changesPrice) return null;
      }
      return Object.fromEntries(values.map(value => {
        return [value, range(matchingVariants(variants, selected, index, value))];
      }));
    });
  }

  function formatLabel(value, prices, money) {
    if (!prices) return value;
    const label = prices.min === prices.max ? money(prices.min) : `${money(prices.min)} - ${money(prices.max)}`;
    return `${value} (${label})`;
  }

  async function loadCatalog(url, fetchPage) {
    const first = await fetchPage(url);
    if (!Number.isInteger(first.pages) || first.pages < 1 || first.pages > 64) throw new Error('Invalid catalog pagination');
    const pages = [first];
    for (let start = 2; start <= first.pages; start += 3) {
      const requests = [];
      for (let page = start; page < start + 3 && page <= first.pages; page += 1) {
        const next = new URL(url);
        next.searchParams.set(first.pageParam, String(page));
        requests.push(fetchPage(next.toString()).then(result => {
          if (result.productId !== first.productId || result.currency !== first.currency || result.page !== page) {
            throw new Error('Mismatched price catalog');
          }
          return result;
        }));
      }
      pages.push(...await Promise.all(requests));
    }
    const variants = pages.flatMap(page => page.variants);
    if (variants.length !== first.total) throw new Error('Incomplete price catalog');
    return variants;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { getOptionRanges, getOptionAvailability, formatLabel, loadCatalog };
  if (typeof window === 'undefined' || window.__preaVariantPricesInstalled) return;
  window.__preaVariantPricesInstalled = true;
  const initialized = new WeakSet();
  const catalogs = new Map();

  async function init(root) {
    if (initialized.has(root)) return;
    const script = root.querySelector('[data-prea-option-price-config]');
    if (!script) return;
    initialized.add(root);
    try {
      const config = JSON.parse(script.textContent);
      let variants = config.variants;
      if (!config.complete) {
        const url = new URL(config.catalogUrl, window.location.origin).toString();
        const key = `${url}:${config.currency}`;
        if (!catalogs.has(key)) {
          const request = loadCatalog(url, async pageUrl => {
            const response = await fetch(pageUrl, { signal: AbortSignal.timeout(15000) });
            if (!response.ok) throw new Error('Unable to load option prices');
            return response.json();
          }).catch(error => { catalogs.delete(key); throw error; });
          catalogs.set(key, request);
        }
        variants = await catalogs.get(key);
      }
      const selects = Array.from(root.querySelectorAll('[data-hc-option-select]'));
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: config.currency, minimumFractionDigits: 2
      });
      const money = cents => formatter.format(cents / 100);
      function update(notify = true) {
        let selected = selects.map(select => select.selectedOptions[0]?.dataset.optionName || '');
        let available = getOptionAvailability(config.optionNames, variants, selected);
        let cleared = false;
        selects.forEach((select, index) => {
          if (selected[index] && !available[index]?.[selected[index]]) {
            select.value = '';
            cleared = true;
          }
        });
        if (cleared) {
          selected = selects.map(select => select.selectedOptions[0]?.dataset.optionName || '');
          available = getOptionAvailability(config.optionNames, variants, selected);
        }
        const ranges = getOptionRanges(config.optionNames, variants, selected);
        selects.forEach((select, index) => {
          for (const option of select.options) {
            const raw = option.dataset.optionName;
            if (!option.value) continue;
            option.hidden = !available[index]?.[raw];
            option.disabled = option.hidden;
            // Native option.label changes display but leaves textContent, value,
            // option-value IDs and customization source data untouched.
            option.label = formatLabel(raw, ranges[index]?.[raw], money);
          }
        });
        if (cleared && notify) selects[0]?.dispatchEvent(new Event('change', { bubbles: true }));
      }
      root.addEventListener('change', event => {
        if (event.target.matches('[data-hc-option-select]')) update(false);
      }, true);
      root.querySelector('[data-prea-reset-options]')?.addEventListener('click', () => {
        selects.forEach(select => { select.value = ''; });
        selects[0]?.dispatchEvent(new Event('change', { bubbles: true }));
      });
      update();
      root.dataset.preaPricesReady = 'true';
    } catch (error) {
      // Never display a price inferred from a truncated or failed catalog.
      root.dataset.preaPricesReady = 'error';
      console.warn('Option prices could not be loaded.', error);
    }
  }

  function scan() {
    document.querySelectorAll('[data-prea-option-prices]').forEach(init);
  }
  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scan);
})();
